import { Fragment, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Linkedin, Twitter, Github } from 'lucide-react';

// Engine Section
export const EngineSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const engineSteps = [
    { label: 'Sensor & Data', icon: '📡', description: 'Real-time data collection' },
    { label: 'Cloud Infrastructure', icon: '☁️', description: 'Scalable processing' },
    { label: 'AI Model', icon: '🧠', description: 'Intelligent analysis' },
    { label: 'Decision Intelligence', icon: '⚡', description: 'Actionable insights' },
    { label: 'Outcome', icon: '🎯', description: 'Measurable impact' },
  ];

  return (
    <section ref={ref} className="py-32 bg-gradient-to-b from-[#0f0f10] to-[#0a0a0b] relative overflow-hidden">
      <div className="absolute inset-0">
        <div className="absolute inset-0" style={{
          backgroundImage: 'linear-gradient(rgba(59, 130, 246, 0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(59, 130, 246, 0.03) 1px, transparent 1px)',
          backgroundSize: '50px 50px'
        }} />
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <motion.div
          className="text-center mb-20"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-5xl md:text-6xl font-bold mb-6">
            The Infrastructure Behind
            <br />
            <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
              Intelligent Nations
            </span>
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Our end-to-end architecture transforms raw data into national-scale outcomes
          </p>
        </motion.div>

        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            {engineSteps.map((step, index) => (
              <Fragment key={step.label}>
                <motion.div
                  className="flex flex-col items-center text-center"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={isInView ? { opacity: 1, scale: 1 } : {}}
                  transition={{ duration: 0.5, delay: index * 0.15 }}
                >
                  <div className="relative mb-4">
                    <div className="w-24 h-24 rounded-full bg-gradient-to-br from-blue-500/20 to-cyan-500/20 border-2 border-white/10 flex items-center justify-center text-4xl backdrop-blur-sm">
                      {step.icon}
                    </div>
                    <div className="absolute -top-2 -right-2 w-8 h-8 bg-cyan-500 rounded-full flex items-center justify-center text-white text-sm font-bold">
                      {index + 1}
                    </div>
                  </div>
                  <div className="text-center">
                    <h3 className="text-lg font-bold mb-2 text-white">{step.label}</h3>
                    <p className="text-sm text-gray-400">{step.description}</p>
                  </div>
                </motion.div>

                {index < engineSteps.length - 1 && (
                  <motion.div
                    className="hidden md:flex items-center"
                    initial={{ opacity: 0, scaleX: 0 }}
                    animate={isInView ? { opacity: 1, scaleX: 1 } : {}}
                    transition={{ duration: 0.5, delay: index * 0.15 + 0.3 }}
                  >
                    <div className="w-12 h-0.5 bg-gradient-to-r from-blue-500 to-cyan-500" />
                    <ArrowRight className="w-5 h-5 text-cyan-400 -ml-2" />
                  </motion.div>
                )}
              </Fragment>
            ))}
          </div>

          <motion.div
            className="mt-20 p-8 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl"
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.8 }}
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="text-4xl font-bold text-cyan-400 mb-2">99.9%</div>
                <p className="text-gray-400">System Uptime</p>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-cyan-400 mb-2">&lt;100ms</div>
                <p className="text-gray-400">API Response Time</p>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-cyan-400 mb-2">Petabyte</div>
                <p className="text-gray-400">Data Processing Scale</p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Footer
export const Footer = () => {
  return (
    <footer className="bg-[#0a0a0b] border-t border-white/5">
      <div className="container mx-auto px-8 lg:px-12 py-16">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12 mb-12">
          {/* Company */}
          <div className="w-full md:pr-6">
            <img src="/logo.png" alt="Invarya" className="h-7 brightness-0 invert mb-6" />
            <p className="text-gray-400 text-sm mb-6">
              Building nation-scale digital infrastructure and resilient ventures for a better tomorrow.
            </p>
            <div className="flex space-x-4">
              <a href="https://www.linkedin.com/company/invarya/about/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/5 hover:bg-white/10 rounded-full flex items-center justify-center transition-all duration-300">
                <Linkedin className="w-5 h-5 text-gray-400 hover:text-white" />
              </a>
              <a href="https://x.com" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/5 hover:bg-white/10 rounded-full flex items-center justify-center transition-all duration-300">
                <Twitter className="w-5 h-5 text-gray-400 hover:text-white" />
              </a>
              <a href="https://github.com" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/5 hover:bg-white/10 rounded-full flex items-center justify-center transition-all duration-300">
                <Github className="w-5 h-5 text-gray-400 hover:text-white" />
              </a>
            </div>
          </div>

          {/* Solutions */}
          <div className="w-full">
            <h4 className="font-semibold mb-6 text-white">Solutions</h4>
            <ul className="space-y-3 text-sm text-gray-400">
              <li><Link to="/" className="hover:text-white transition-colors">Emerging Technology</Link></li>
              <li><Link to="/" className="hover:text-white transition-colors">Product Design</Link></li>
              <li><Link to="/" className="hover:text-white transition-colors">Business Transformation</Link></li>
              <li><Link to="/" className="hover:text-white transition-colors">Enablement</Link></li>
            </ul>
          </div>

          {/* Platforms */}
          <div className="w-full">
            <h4 className="font-semibold mb-6 text-white">Platforms</h4>
            <ul className="space-y-3 text-sm text-gray-400">
              <li><a href="https://maps.guru/" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Maps.Guru</a></li>
              <li><a href="https://conv-earth.pronit.workers.dev/" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Conv.Earth</a></li>
            </ul>
          </div>

        </div>

        <div className="max-w-5xl mx-auto pt-8 border-t border-white/5 flex flex-col md:flex-row justify-between items-center text-sm text-gray-400">
          <p className="text-gray-400">&copy; 2024 Invarya. All rights reserved.</p>
          <div className="flex space-x-6 mt-4 md:mt-0">
            <Link to="/" className="hover:text-white transition-colors">Privacy Policy</Link>
            <Link to="/" className="hover:text-white transition-colors">Terms of Service</Link>
            <Link to="/" className="hover:text-white transition-colors">Trust Center</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default { EngineSection, Footer };
